import { Pagination } from "../../utils/pagination-hooks";
import { Filter, Warehouse } from "../../models/table-models";
import { resourceCache, useAsyncResource } from "use-async-resource";
import {
    useGetSingleDistributorReference,
    useGetSingleWarehouseReference,
    useGetWarehouses
} from "../../utils/warehouses-hooks";
import { useGetUsers } from "../../utils/users-hooks";
import StepWizard from "react-step-wizard";
import { CustomStepWizardNav } from "../../components/CustomStepWizardNav";
import React, { Suspense, useCallback, useMemo, useState } from "react";
import { WarehouseReferences } from "./WarehouseReferences";
import { UserList } from "./UserList";
import { Spinner } from "react-bootstrap";

interface TabInfo {
    type: 'warehouse' | 'distributor';
    id: string;
    name: string;
}

// Component that fetches and displays a SINGLE warehouse's references
const SingleWarehouseLoader = ({
    warehouseId,
    warehouseName,
    userIds,
    filter,
    pagination,
    showAll,
}: {
    warehouseId: string;
    warehouseName: string;
    userIds: string[];
    filter: Filter;
    pagination: Pagination;
    showAll: boolean;
}) => {
    const [getReferences, fetchNewReferences] = useAsyncResource(
        useGetSingleWarehouseReference,
        warehouseId,
        userIds,
        filter,
        showAll
    );

    const referencesData = getReferences();

    // The API returns an array, but for single warehouse it should have 1 item
    const warehouseData = referencesData?.[0];

    const refetchReferencesHook = async () => {
        resourceCache(useGetSingleWarehouseReference).clear();
        await fetchNewReferences(warehouseId, userIds, filter, showAll);
    };

    if (!warehouseData) {
        return <div className="p-4">No data available for {warehouseName}</div>;
    }

    return (
        <WarehouseReferences
            {...{ pagination, filter }}
            items={warehouseData.itemsForWarehouse}
            warehouse={warehouseData.warehouse || warehouseName}
            refetchEntitiesHook={refetchReferencesHook}
            distributorDays={[]}
            showAll={showAll}
        />
    );
};

// Component that fetches and displays a SINGLE distributor's references
const SingleDistributorLoader = ({
    userId,
    filter,
    pagination,
    selectedState,
    selectedType,
    showAll,
}: {
    userId: string;
    filter: Filter;
    pagination: Pagination;
    selectedState: string[];
    selectedType?: string;
    showAll: boolean;
}) => {
    const [getReferences, fetchNewReferences] = useAsyncResource(
        useGetSingleDistributorReference,
        userId,
        filter,
        selectedState.toString(),
        selectedType,
        showAll
    );

    const referencesData = getReferences();

    // The API returns an array, but for single user it should have 1 item
    const userData = referencesData?.[0];

    const refetchDistributorReferencesHook = async () => {
        resourceCache(useGetSingleDistributorReference).clear();
        await fetchNewReferences(userId, filter, selectedState.toString(), selectedType, showAll);
    };

    if (!userData) {
        return <div className="p-4">No data available for this distributor</div>;
    }

    return (
        <UserList
            references={userData.references}
            filter={filter}
            pagination={pagination}
            refetchDistributorReferencesHook={refetchDistributorReferencesHook}
        />
    );
};

// Main component with lazy loading per tab
export const InventoryReferences = ({
    pagination,
    filter,
    selectedWarehouses,
    selectedDistributors,
    selectedState,
    selectedType,
    showAll
}: {
    pagination: Pagination;
    filter: Filter;
    selectedWarehouses: string[];
    selectedDistributors: string[];
    selectedState: string[];
    selectedType?: string;
    showAll: boolean;
}) => {
    // Track which tab is currently active (1-indexed for StepWizard)
    // const [activeStep, setActiveStep] = useState(1);

    // Track which tabs have been visited (to keep their data loaded)
    const [visitedTabs, setVisitedTabs] = useState<Set<number>>(new Set([1]));

    // Fetch warehouse and user metadata for display names
    const [getWarehouses] = useAsyncResource(useGetWarehouses, {});
    const [getUsers] = useAsyncResource(useGetUsers, undefined);

    const warehousesData = getWarehouses().items;
    const usersData = getUsers().items;

    // Build the list of tabs: warehouses first, then distributors
    const tabs: TabInfo[] = useMemo(() => {
        const result: TabInfo[] = [];

        // Add warehouse tabs
        selectedWarehouses.forEach((whId) => {
            const warehouse = warehousesData?.find((w: Warehouse) => w.id?.toString() === whId);
            result.push({
                type: 'warehouse',
                id: whId,
                name: warehouse?.warehouseName || `Warehouse ${whId}`,
            });
        });

        // Add distributor tabs
        selectedDistributors.forEach((userId) => {
            const user = usersData?.find((u: any) => u.id?.toString() === userId);
            result.push({
                type: 'distributor',
                id: userId,
                name: user?.username || `Distributor ${userId}`,
            });
        });

        return result;
    }, [selectedWarehouses, selectedDistributors, warehousesData, usersData]);

    // Handler for when the step changes
    const handleStepChange = useCallback((stats: { activeStep: number }) => {
        // setActiveStep(stats.activeStep);
        setVisitedTabs((prev) => new Set([...Array.from(prev), stats.activeStep]));
    }, []);

    // Render a tab's content only if it's been visited
    const renderTabContent = (tab: TabInfo, tabIndex: number) => {
        const stepNumber = tabIndex + 1; // StepWizard is 1-indexed
        const isVisited = visitedTabs.has(stepNumber);

        // If this tab hasn't been visited yet, show placeholder
        if (!isVisited) {
            return (
                <div key={`placeholder-${tab.id}`} className="p-4 text-center">
                    <p>Click this tab to load data for {tab.name}</p>
                </div>
            );
        }

        // Tab has been visited, render the actual content with Suspense
        if (tab.type === 'warehouse') {
            return (
                <Suspense
                    key={`warehouse-${tab.id}`}
                    fallback={
                        <div className="p-4 text-center">
                            <Spinner animation='border' />
                            <p>Loading {tab.name}...</p>
                        </div>
                    }
                >
                    <SingleWarehouseLoader
                        warehouseId={tab.id}
                        warehouseName={tab.name}
                        userIds={selectedDistributors}
                        filter={filter}
                        pagination={pagination}
                        showAll={showAll}
                    />
                </Suspense>
            );
        } else {
            return (
                <Suspense
                    key={`distributor-${tab.id}`}
                    fallback={
                        <div className="p-4 text-center">
                            <Spinner animation='border' />
                            <p>Loading {tab.name}...</p>
                        </div>
                    }
                >
                    <SingleDistributorLoader
                        userId={tab.id}
                        filter={filter}
                        pagination={pagination}
                        selectedState={selectedState}
                        selectedType={selectedType}
                        showAll={showAll}
                    />
                </Suspense>
            );
        }
    };

    // If no tabs, show empty state
    if (tabs.length === 0) {
        return (
            <div className="p-4 text-center">
                <p>No warehouses or distributors to display.</p>
            </div>
        );
    }

    return (
        <StepWizard
            className={'w-100'}
            nav={
                <CustomStepWizardNav
                    steps={tabs.map(tab => tab.name)}
                    translate={false}
                />
            }
            transitions={{
                enterRight: '',
                enterLeft: '',
                exitRight: '',
                exitLeft: '',
            }}
            onStepChange={handleStepChange}
        >
            {tabs.map((tab, index) => (
                <div key={`step-${tab.type}-${tab.id}`}>
                    {renderTabContent(tab, index)}
                </div>
            ))}
        </StepWizard>
    );
};
